import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Button, Logo } from '../components/primitives';

export function LoginRoute() {
  const auth = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  if (auth.status === 'auth') {
    if (auth.role === 'admin') {
      // Auth context will block; just bounce to /
      return <Navigate to="/" replace state={{ from: location }} />;
    }
    if (auth.role === 'owner' || auth.role === 'staff') {
      return <Navigate to="/ops" replace state={{ from: location }} />;
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setLocalError(null);
    try {
      await auth.login(email, password);
    } catch (err) {
      setLocalError((err as Error).message ?? '로그인에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--bg-page)',
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-weak)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-md)',
          padding: 28,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Logo size={26} />
          <span
            style={{
              fontSize: 11,
              color: 'var(--fg-3)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            사장 콘솔
          </span>
        </div>
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              margin: 0,
              letterSpacing: '-0.02em',
              color: 'var(--fg-1)',
            }}
          >
            로그인
          </h1>
          <p
            style={{
              fontSize: 13,
              color: 'var(--fg-3)',
              marginTop: 6,
              marginBottom: 0,
            }}
          >
            가입 승인된 회사의 owner / staff 만 진입할 수 있습니다.
          </p>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--fg-2)',
            }}
          >
            이메일
          </span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-base)',
              borderRadius: 8,
              fontSize: 14,
              background: 'var(--bg-surface)',
              color: 'var(--fg-1)',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--fg-2)',
            }}
          >
            비밀번호
          </span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-base)',
              borderRadius: 8,
              fontSize: 14,
              background: 'var(--bg-surface)',
              color: 'var(--fg-1)',
            }}
          />
        </label>

        {(localError || auth.error) && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--danger)',
              background: 'var(--danger-bg)',
              borderRadius: 8,
              padding: '8px 12px',
            }}
          >
            {localError ?? auth.error}
          </div>
        )}

        <Button
          type="submit"
          variant="strong"
          size="lg"
          disabled={busy || !email || !password}
        >
          {busy ? '로그인 중…' : '로그인'}
        </Button>

        <div
          style={{
            fontSize: 12,
            color: 'var(--fg-3)',
            textAlign: 'center',
          }}
        >
          아직 회사가 없으신가요?{' '}
          <Link to="/signup" style={{ color: 'var(--fg-1)', fontWeight: 600 }}>
            가입 신청
          </Link>
        </div>
      </form>
    </main>
  );
}
